import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faChevronDown,
  faEye,
  faCamera,
} from "@fortawesome/free-solid-svg-icons";
import { Link, useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../firebase.config";

const SignUpForm = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [department, setDepartment] = useState("");
  const [club, setClub] = useState("");
  const [role, setRole] = useState("");
  const [year, setYear] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (
      file &&
      (file.type === "image/png" ||
        file.type === "image/jpeg" ||
        file.type === "image/jpg")
    ) {
      setProfileImage(file);
    } else {
      setError("Please upload a PNG, JPG, or JPEG image.");
    }
  };

  const CreateUser = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    setError("");
    setSuccess("");

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      let imageUrl = "";
      if (profileImage) {
        const imageRef = ref(
          storage,
          `profileImages/${userCredential.user.uid}`
        );
        await uploadBytes(imageRef, profileImage);
        imageUrl = await getDownloadURL(imageRef);
      }

      const userInfo = {
        name,
        email,
        department,
        club,
        role,
        year,
        userId: userCredential.user.uid,
        profileImageUrl: imageUrl,
      };

      await setDoc(doc(db, "users", userCredential.user.uid), userInfo);
      console.log("User info saved to the database");
      localStorage.setItem("userid", JSON.stringify(userInfo.userId));
      setSuccess("Account created successfully");
      navigate("/");
    } catch (error) {
      console.log(error);
      setError("Failed to create account. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <header className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <img src="" alt="Logo" />
            <span className="font-semibold">Metaverse</span>
          </div>
          <nav className="text-gray-400 text-sm">
            <Link to="/" className="mr-4">
              Home
            </Link>
          </nav>
        </header>

        <main>
          <h2 className="text-sm font-semibold text-gray-400 mb-2">
            Join the Team
          </h2>
          <h1 className="text-4xl font-bold mb-2">
            Create new account<span className="text-blue-500">.</span>
          </h1>
          <p className="text-sm text-gray-400 mb-8">
            Already A Member?{" "}
            <Link to="/login" className="text-blue-500">
              Log In
            </Link>
          </p>
          {error && (
            <div className="bg-red-500 text-white p-3 mb-4 rounded-lg">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-500 text-white p-3 mb-4 rounded-lg">
              {success}
            </div>
          )}
          <form onSubmit={CreateUser}>
            <div className="flex gap-4 mb-4">
              <div className="flex-1 bg-gray-800 rounded-lg p-3 flex items-center">
                <input
                  type="text"
                  placeholder="Name"
                  className="bg-transparent w-full outline-none"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <FontAwesomeIcon icon={faUser} className="text-gray-500" />
              </div>
              <div className="flex-1 bg-gray-800 rounded-lg p-3 flex items-center">
                <input
                  type="email"
                  placeholder="Email"
                  className="bg-transparent w-full outline-none"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <FontAwesomeIcon icon={faUser} className="text-gray-500" />
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-3 flex items-center mb-4">
              <input
                type="password"
                placeholder="Password"
                className="bg-transparent w-full outline-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <FontAwesomeIcon
                icon={faEye}
                className="text-gray-500 cursor-pointer"
              />
            </div>

            <div className="bg-gray-800 rounded-lg p-3 flex items-center mb-4">
              <select
                className="bg-gray-800 w-full outline-none appearance-none text-white"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
              >
                <option value="">Department</option>
                <option value="CSE">CSE</option>
                <option value="Cyber">Cyber</option>
                <option value="CSD">CSD</option>
                <option value="AIDS">AIDS</option>
              </select>
              <FontAwesomeIcon icon={faChevronDown} className="text-gray-500" />
            </div>

            <div className="bg-gray-800 rounded-lg p-3 flex items-center mb-4">
              <select
                className="bg-gray-800 w-full outline-none appearance-none text-white"
                value={club}
                onChange={(e) => setClub(e.target.value)}
              >
                <option value="">Club</option>
                <option value="Fullstack">Fullstack</option>
                <option value="AI">AI</option>
                <option value="AR">AR</option>
                <option value="VR">VR</option>
                <option value="Hardware">Hardware</option>
                <option value="Cyber">Cyber</option>
              </select>
              <FontAwesomeIcon icon={faChevronDown} className="text-gray-500" />
            </div>

            <div className="bg-gray-800 rounded-lg p-3 flex items-center mb-4">
              <select
                className="bg-gray-800 w-full outline-none appearance-none text-white"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="">Role</option>
                <option value="chairperson">Chairperson</option>
                <option value="secretary">Secretary</option>
                <option value="jointsecretary">Joint Secretary</option>
                <option value="leaddesigner">Lead Designer</option>
                <option value="treasurer">Treasurer</option>
                <option value="member">Member</option>
              </select>
              <FontAwesomeIcon icon={faChevronDown} className="text-gray-500" />
            </div>

            <div className="bg-gray-800 rounded-lg p-3 flex items-center mb-4">
              <select
                className="bg-gray-800 w-full outline-none appearance-none text-white"
                value={year}
                onChange={(e) => setYear(e.target.value)}
              >
                <option value="">Year</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
              </select>
              <FontAwesomeIcon icon={faChevronDown} className="text-gray-500" />
            </div>

            <div className="bg-gray-800 rounded-lg p-3 flex items-center mb-4">
              <input
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handleImageUpload}
                ref={fileInputRef}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current.click()}
                className="bg-transparent w-full text-left outline-none text-gray-400"
              >
                {profileImage
                  ? profileImage.name
                  : "Upload Profile Image (PNG, JPG, JPEG)"}
              </button>
              <FontAwesomeIcon icon={faCamera} className="text-gray-500" />
            </div>

            <div className="flex justify-between items-center">
              <button
                type="submit"
                className="bg-blue-500 text-white px-6 py-2 rounded-lg disabled:opacity-50"
                disabled={isCreating}
              >
                {isCreating ? "Creating account..." : "Create account"}
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

export default SignUpForm;



